package ai.jessie.web.projects

import ai.jessie.shared.projects.SaveProjectRequest
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

/** The slide-over heading while the review is shown; [projectReview] fills the panel body. */
@Composable
internal fun projectReviewHeading(state: ProjectUiState) {
    H2(attrs = { id(PROJECT_REVIEW_TITLE_ID) }) {
        Text(
            if (state.current == null) "Neues Projekt prüfen und bestätigen"
            else "Änderungen prüfen und bestätigen"
        )
    }
}

// Step 11 and A1: the exact request that confirmation sends. Nothing is saved until the member
// confirms; cancelling (A10) sends nothing. The buttons live in the slide-over footer.
@Composable
internal fun projectReview(state: ProjectUiState, focusReturn: FocusReturn) {
    val proposal = state.pending ?: return
    confirmationDialog(proposal, focusReturn) { proposedProject(proposal, state) }
}

// A4, A8 and A9: the selected record and the consequence, before anything is sent.
@Composable
internal fun projectChangeConfirmation(state: ProjectUiState, actions: ProjectActions) {
    val change = state.change ?: return
    val current = state.current ?: return
    val position = current.positions.firstOrNull { it.details.id == change.positionId }
    val target =
        if (change.positionId == null) "Projekt «${current.name}»"
        else "Position «${position?.details?.name ?: "–"}»"
    val deactivating = change.operation == ProjectOperation.DEACTIVATE
    Div(attrs = CardStyle.toAttrs()) {
        confirmationDialog(change, focusReturn = null) {
            Div(attrs = CardBodyStyle.toAttrs()) {
                H2(attrs = { id(PROJECT_REVIEW_TITLE_ID) }) {
                    Text(
                        if (deactivating) "Deaktivierung bestätigen"
                        else "Endgültiges Entfernen bestätigen"
                    )
                }
                P {
                    Text(
                        if (deactivating)
                            "$target nimmt danach keine neue Arbeit mehr an. Bestehende " +
                                "Arbeits-, Rechnungs- und weitere Verknüpfungen bleiben erhalten."
                        else
                            "$target wird endgültig entfernt. Das ist nur möglich, wenn der " +
                                "Eintrag nie verwendet wurde; andernfalls bleibt er erhalten und " +
                                "kann deaktiviert werden."
                    )
                }
            }
            Div(attrs = CardFootStyle.toAttrs()) {
                themedButton("Abbrechen", actions.cancel, disabled = state.busy)
                themedButton(
                    if (deactivating) "Deaktivierung bestätigen" else "Endgültig entfernen",
                    actions.confirmChange,
                    variant = if (deactivating) ButtonVariant.PRIMARY else ButtonVariant.DANGER,
                    disabled = state.busy,
                )
            }
        }
    }
}

// The confirmation takes focus when it opens; a later view returns focus to the page title.
@Composable
private fun confirmationDialog(
    subject: Any,
    focusReturn: FocusReturn?,
    content: @Composable () -> Unit,
) {
    Div(
        attrs = {
            id(REVIEW_ID)
            attr("role", "alertdialog")
            attr("aria-labelledby", PROJECT_REVIEW_TITLE_ID)
            attr("tabindex", "-1")
        }
    ) {
        content()
    }
    LaunchedEffect(subject) {
        focusReturn?.pending = true
        if (!AppGlobals.isExporting) (document.getElementById(REVIEW_ID) as? HTMLElement)?.focus()
    }
}

@Composable
private fun proposedProject(proposal: SaveProjectRequest, state: ProjectUiState) {
    val customers = state.customerOptions()
    val vatRates = state.vatOptions(state.current?.positions.orEmpty())
    Div(attrs = StackStyle.toAttrs()) {
        TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
            definition("Name", proposal.name)
            definition("Beschreibung", proposal.description ?: "–")
            definition(
                "Kunde",
                proposal.customerId?.let { customers[it] ?: "–" } ?: "Kein Kunde",
            )
            definition(
                "Status",
                "${activeLabel(proposal.active)} · ${billableLabel(proposal.billable)}",
            )
        }
        H3 { Text("Positionen") }
        Ul(attrs = SubListStyle.toAttrs()) {
            proposal.positions.forEach { Li { Text(it.summary(it.vatRateId?.let(vatRates::get))) } }
        }
    }
}

@Composable
private fun definition(term: String, value: String) {
    TagElement<HTMLElement>("dt", null) { Text(term) }
    TagElement<HTMLElement>("dd", null) { Text(value) }
}

internal const val PROJECT_REVIEW_TITLE_ID = "project-review-title"
private const val REVIEW_ID = "project-review"
